<template>
    <ProInputGroup v-bind="$attrs" v-slot="{ id, attrs, hasErrors }">
        <div
            class="support-textarea"
            :class="{ 'support-textarea--highlighted': highlighted }"
            :style="{ '--drop-files-here': `'${$t('support.drop_files_here')}'` }"
        >
            <textarea
                class="support-textarea__input"
                :id="id"
                ref="textarea"
                :value="value"
                @input="input"
                :aria-describedby="hasErrors ? `${id}-error` : null"
                v-bind="{ ...{ autocomplete: 'off' }, ...attrs }"
            ></textarea>
            <div class="support-textarea__files">
                <input id="support-files" type="file" ref="file" multiple style="display: none" @change="attachFiles" />
                <div class="support-file" v-for="(file, index) in internalFiles">
                    <a
                        class="support-file__name"
                        v-if="file.complete"
                        :href="`https://savage-support-request-files.s3-ap-southeast-2.amazonaws.com/${file.key}`"
                        target="_blank"
                        >{{ file.name }}</a
                    >
                    <span class="support-file__name" v-else>{{ file.name }}</span>
                    <div class="support-file__actions">
                        <span class="support-file__failed" v-if="file.failed">{{ $t("global.failed") }}</span>
                        <div
                            class="support-file__progress"
                            role="progressbar"
                            :aria-valuenow="file.progress"
                            aria-valuemin="0"
                            aria-valuemax="100"
                            v-else-if="!file.complete"
                            :style="{ '--progress': `${file.progress}%` }"
                        >
                            <span class="sr-only">{{ file.progress }}%</span>
                        </div>
                        <button class="support-file__remove" type="button" @click="deleteFile(index)" title="Remove file"><ProIcon icon="close" /></button>
                    </div>
                </div>
                <div class="support-file">
                    <i18n
                        v-if="!internalFiles.length"
                        path="support.attach_files"
                        tag="button"
                        @click="$refs.file.click()"
                        type="button"
                        aria-label="Attach a file"
                        class="support-textarea__submit-files"
                    >
                        <template v-slot:select_files
                            ><span>{{ $t("support.select_files") }}</span></template
                        >
                    </i18n>
                    <button class="support-textarea__submit-files" @click="$refs.file.click()" type="button" title="Attach a file" v-else>
                        <span>{{ $t("support.attach_another_file") }}</span>
                    </button>
                </div>
            </div>
        </div>
    </ProInputGroup>
</template>

<script>
import tippy from "tippy.js";
import axios from "axios";
import ProInputGroup from "~/components/ProInputGroup";

export default {
    inheritAttrs: false,
    components: { ProInputGroup },
    props: {
        value: String,
        files: Array,
    },
    data() {
        return {
            internalFiles: this.files,
            highlighted: false,
        };
    },
    methods: {
        input(event) {
            this.$emit("input", event.target.value);
            this.resizeTextarea(event);
        },

        focus() {
            this.$refs.textarea.focus();
        },

        blur() {
            this.$refs.textarea.blur();
        },

        select() {
            this.$refs.textarea.select();
        },

        setSelectionRange(start, end) {
            this.$refs.textarea.setSelectionRange(start, end);
        },

        clear() {
            this.$refs.textarea.value = "";
            this.$emit("input", "");
        },

        resizeTextarea() {
            this.$refs.textarea.style.height = "auto";
            this.$refs.textarea.style.height = this.$refs.textarea.scrollHeight + "px";
        },

        initDropzone() {
            ["drag", "dragstart", "dragend", "dragover", "dragenter", "dragleave", "drop"].forEach((eventName) => {
                document.addEventListener(eventName, this.preventDefaults, false);
            });

            document.addEventListener("dragenter", this.handleDragEnter, false);
            document.addEventListener("dragleave", this.handleDragLeave, false);
            document.addEventListener("drop", this.handleDrop, false);
        },

        destroyDropzone() {
            ["drag", "dragstart", "dragend", "dragover", "dragenter", "dragleave", "drop"].forEach((eventName) => {
                document.removeEventListener(eventName, this.preventDefaults, false);
            });

            document.removeEventListener("dragenter", this.handleDragEnter, false);
            document.removeEventListener("dragleave", this.handleDragLeave, false);
            document.removeEventListener("drop", this.handleDrop, false);
        },

        handleDragEnter() {
            this.highlighted = true;
        },

        handleDragLeave(event) {
            if (event.screenY <= 0 && event.screenX <= 0) {
                this.highlighted = false;
            }
        },

        handleDrop(event) {
            this.highlighted = false;

            if (event.dataTransfer.files.length) {
                this.handleFiles([...event.dataTransfer.files]);
            }
        },

        preventDefaults(event) {
            event.preventDefault();
            event.stopPropagation();
        },

        attachFiles(e) {
            if (e.target.files.length) {
                this.handleFiles([...e.target.files]);
            }

            e.target.value = ""; // Reset input
        },

        async handleFiles(files) {
            files.forEach((f) => {
                let file = {
                    name: f.name,
                    size: f.size,
                    type: f.type,
                    key: "",
                    signed_url: "",
                    progress: 0,
                    complete: false,
                    failed: false,
                    cancel: () => {},
                };

                (async () => {
                    try {
                        const ref = await this.createFileReference(file.name);
                        file.key = ref.key;
                        file.signed_url = ref.signed_url;
                        await this.uploadFile(f, file);
                    } catch (e) {
                        file.failed = true;
                        console.error(e);
                    }
                })();

                this.files.push(file);
            });
        },

        async createFileReference(name) {
            const recaptcha_token = await this.$recaptcha.execute("marketing/support_form/file");

            const { data } = await this.$adminApi.post("/product-care/support-requests/files", {
                name: name,
                recaptcha: recaptcha_token,
            });

            return data;
        },

        async uploadFile(file, ref) {
            const source = axios.CancelToken.source();

            ref.cancel = () => {
                source.cancel("File upload canceled by user");
            };

            const res = await axios.put(ref.signed_url, file, {
                headers: {
                    "Content-Type": file.type,
                },
                cancelToken: source.token,
                onUploadProgress: (progressEvent) => {
                    ref.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                },
            });

            ref.complete = true;

            return res;
        },

        deleteFile(i) {
            const file = this.files[i];
            if (!file.complete) file.cancel();
            this.files.splice(i, 1);
        },

        formatBytes(bytes, decimals = 2) {
            if (bytes === 0) return "0 Bytes";

            const k = 1024;
            const dm = decimals < 0 ? 0 : decimals;
            const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

            const i = Math.floor(Math.log(bytes) / Math.log(k));

            return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
        },
    },

    watch: {
        value() {
            this.resizeTextarea();
        },

        internalFiles() {
            this.$emit("files", this.files);
        },
    },

    mounted() {
        this.resizeTextarea();

        if (this.help) {
            tippy(this.$refs.help, {
                content: this.help,
            });
        }

        this.initDropzone();
    },

    beforeDestroy() {
        this.destroyDropzone();
    },
};
</script>
